import { apiRequest } from './index';
import logJsUnexpected from '../telemetry/logJsUnexpected';

export const API_URL = '/candidate/create-from-search';

type EmailType = '' | 'repeat' | 'new';
type ErrorType = '' | 'reg-limit-reached' | 'email_exists' | 'invalid_email' | 'bad-url';
type PasswordType = '' | 'system_generated' | 'google_signin' | 'user_generated' | 'undefined';
type PerlBoolean = 0 | 1;
type ScreenType = '' | 'serps_reg_modal' | 'job_page_reg_modal';
type StatusType = '' | 'error' | 'warning' | 'success';

interface CreateFromSearchParams {
  emailAddress: string;
  encryptedQuizId?: string;
  nextUrl?: string;
  applyAfterReg?: string,
  emailExistsRedirect?: string,
  lvk?: string,
  method: string;
}

/* eslint-disable camelcase */
interface CreateFromSearchResponse {
  contact_id: string;
  error_type: ErrorType;
  message: string;
  options?: { refresh_page?: PerlBoolean };
  redirect_url: string;
  si_email_type: EmailType;
  si_password_type: PasswordType;
  si_screen_type: ScreenType;
  status: StatusType;
  success: PerlBoolean;
}
/* eslint-enable camelcase */

/**
 * Calls the candidate/create-from-search endpoint to create a JobSeeker from the provided email address
 *
 * @param {string} options.emailAddress contains the email address the JobSeeker wants to use to sign up on the site
 * @param {object} options.createFromSearchParameters contains information like the jobseeker's search query,
 * location search, etc.
 * @returns json response
 */
export async function candidateCreateFromSearch({
  emailAddress,
  encryptedQuizId,
  nextUrl,
  method,
  applyAfterReg,
  lvk,
  emailExistsRedirect,
}: CreateFromSearchParams): Promise<CreateFromSearchResponse> {
  if (!emailAddress) {
    // Error out if `emailAddress` is not present because it is required by
    // the `/candidate/create-from-search` endpoint to create a Jobseeker
    const errorMessage = 'Email address is required for candidateCreateFromSearch';
    logJsUnexpected(errorMessage);
    throw new Error(errorMessage);
  }

  const params = new URLSearchParams({
    email_address: emailAddress,
    encrypted_id: encryptedQuizId || '',
    next_url: nextUrl || '',
    lvk: lvk || '',
    apply_after_reg: applyAfterReg || '',
    email_exists_redirect: emailExistsRedirect || '',
    method,
  });

  const fetchInit = {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
    },
    body: params.toString(),
  };

  try {
    const response = await apiRequest(API_URL, fetchInit, undefined, true);
    const responseJson = await response.json() as Promise<CreateFromSearchResponse>;
    return responseJson;
  } catch (e) {
    logJsUnexpected(`Error calling ${API_URL}`);
    throw e;
  }
}

export default candidateCreateFromSearch;
